<template>
  <div class="panic-app">
    <AppHeader />
    <CurrentLocation @position-changed="handlePositionChanged" />
    <main class="panic-container">
      <!-- Floating Hotlines Button -->
      <button
        class="hotlines-btn floating-hotlines-btn"
        @click="showHotlines = true"
        :class="{ hidden: isActivated || showHotlines }"
      >
        <i class="fas fa-phone"></i>
        <span>Emergency Hotlines</span>
      </button>

      <!-- Panic Button -->
      <div
        class="panic-button-wrapper"
        @touchstart="startPress"
        @touchend="endPress"
        @mousedown="startPress"
        @mouseup="endPress"
        @mouseleave="endPress"
      >
        <div
          class="panic-button"
          :class="{
            pressing: isPressing,
            activated: isActivated,
            'pulse-animation': pulseAnimation,
          }"
          @animationend="pulseAnimation = false"
        >
          <div class="button-inner" style="user-select: none; -webkit-user-select: none">
            <span v-if="!isActivated">HOLD FOR HELP</span>
            <span v-else>WHAT'S YOUR EMERGENCY?</span>
            <div class="overlay-blocker"></div>
          </div>
          <div
            class="press-progress"
            :style="{ width: pressProgress + '%' }"
            v-if="isPressing && !isActivated"
          ></div>
        </div>
      </div>

      <!-- Emergency Type Selection -->
      <transition name="slide-up">
        <div class="emergency-options" v-if="isActivated && !emergencyTypeSelected">
          <h2>What type of assistance do you need?</h2>
          <div class="option-grid">
            <button
              class="emergency-option medical"
              @click="selectEmergencyType(1)"
              :class="{ selected: selectedTypes.includes(1) }"
            >
              <i class="fa-solid fa-truck-medical text-danger"></i>
              <span>Medical</span>
            </button>
            <button
              class="emergency-option police"
              @click="selectEmergencyType(2)"
              :class="{ selected: selectedTypes.includes(2) }"
            >
              <i class="fas fa-shield-alt"></i>
              <span>Police</span>
            </button>
            <button
              class="emergency-option fire"
              @click="selectEmergencyType(3)"
              :class="{ selected: selectedTypes.includes(3) }"
            >
              <i class="fas fa-fire-extinguisher"></i>
              <span>Fire</span>
            </button>
          </div>
          <button
            class="confirm-btn"
            @click="confirmEmergencyTypes"
            :disabled="selectedTypes.length === 0"
          >
            Confirm ({{ selectedTypes.length }} selected)
          </button>
        </div>
      </transition>

      <!-- Emergency Details -->
      <transition name="slide-up">
        <div class="emergency-details" v-if="emergencyTypeSelected">
          <h2>Please provide more details</h2>
          <div class="detail-section">
            <label>What is the emergency?</label>
            <select v-model="emergencyDetail" class="detail-select">
              <option value="">Select an option</option>
              <option
                v-for="(option, index) in filteredEmergencyOptions"
                :key="index"
                :value="option.value"
              >
                {{ option.text }}
              </option>
            </select>
          </div>
          <div class="detail-section">
            <label>Additional information (optional)</label>
            <textarea
              v-model="additionalInfo"
              placeholder="Describe the emergency situation..."
              class="detail-textarea"
            ></textarea>
          </div>
          <button class="submit-btn" @click="submitEmergency">
            Submit Emergency Request
          </button>
        </div>
      </transition>

      <!-- Hotlines Section -->
      <transition name="fade-slide">
        <div class="hotlines-section" v-if="showHotlines">
          <h2>Emergency Hotlines</h2>
          <div class="hotline-cards">
            <div class="hotline-card medical">
              <i class="fa-solid fa-truck-medical"></i>
              <h3>PERT</h3>
              <a href="tel:911" class="hotline-number">911</a>
              <a href="#" class="messenger-link">
                <i class="fab fa-facebook-messenger"></i>
                <span>Message</span>
              </a>
            </div>
            <div class="hotline-card police">
              <i class="fas fa-shield-alt"></i>
              <h3>Police</h3>
              <a href="tel:09253434344" class="hotline-number"
                ><i class="fas fa-phone"></i> 09253434344</a
              >
              <a href="#" class="messenger-link">
                <i class="fab fa-facebook-messenger"></i>
                <span>Message</span>
              </a>
            </div>
            <div class="hotline-card fire">
              <i class="fas fa-fire-extinguisher"></i>
              <h3>Fire Department</h3>
              <a href="tel:911" class="hotline-number">911</a>
              <a href="#" class="messenger-link">
                <i class="fab fa-facebook-messenger"></i>
                <span>Message</span>
              </a>
            </div>
          </div>
          <button class="back-btn" @click="showHotlines = false">
            Back to Emergency
          </button>
        </div>
      </transition>
      <div
        class="prank-warning text-center d-flex flex-column align-items-center justify-content-center px-3 py-4"
      >
        <i class="fas fa-exclamation-triangle fa-2x text-danger mb-2"></i>
        <h5 class="text-danger fw-bold mb-2">Serious Warning</h5>
        <p class="mb-0 text-dark fw-medium">
          Making prank reports is a criminal offense. Under existing laws, those who abuse
          this emergency system may face legal consequences including imprisonment and
          fines.
          <br />
          Authorities will **track, investigate, and prosecute** pranksters to the full
          extent of the law.
        </p>
      </div>
    </main>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import AppHeader from "@/components/AppHeader.vue";
import CurrentLocation from "@/components/CurrentLocation.vue";
import emergencyService from "@/services/emergencyService";
import toastService from "@/services/toastService";
import spinnerService from "@/services/spinnerService";
// Reactive state
const isPressing = ref(false);
const isActivated = ref(false);
const pressProgress = ref(0);
const pressInterval = ref(null);
const pulseAnimation = ref(false);
const selectedTypes = ref([]);
const emergencyTypeSelected = ref(false);
const emergencyDetail = ref("");
const additionalInfo = ref("");
const showHotlines = ref(false);
const currentLocation = ref(null);
const emergencyOptions = [
  { value: 1, text: "Vehicular Accident", types: [1, 2] },
  { value: 2, text: "Crime in Progress", types: [2] },
  { value: 3, text: "Fire Emergency", types: [3] },
  { value: 4, text: "Medical Emergency", types: [1] },
  { value: 5, text: "Assault/Violence", types: [1, 2] },
  { value: 6, text: "Heart Attack/Stroke", types: [1] },
  { value: 7, text: "Other Emergency", types: [1, 2, 3] },
];

// Computed properties
const filteredEmergencyOptions = computed(() => {
  if (selectedTypes.value.length === 0) return emergencyOptions;

  return emergencyOptions.filter((option) =>
    // Check if ALL selected types are included in this option's types
    selectedTypes.value.every((selectedType) => option.types.includes(selectedType))
  );
});
const handlePositionChanged = (pos) => {
  currentLocation.value = pos;
};

// Methods
const startPress = () => {
  if (isActivated.value) return;

  isPressing.value = true;
  pressProgress.value = 0;
  pulseAnimation.value = true;

  pressInterval.value = setInterval(() => {
    pressProgress.value += 3.33;
    if (pressProgress.value >= 100) {
      activatePanic();
    }
  }, 100);
};

const endPress = () => {
  if (!isPressing.value || isActivated.value) return;

  clearInterval(pressInterval.value);
  isPressing.value = false;
  pressProgress.value = 0;
};

const activatePanic = () => {
  clearInterval(pressInterval.value);
  isPressing.value = false;
  isActivated.value = true;
  pulseAnimation.value = true;
};

const selectEmergencyType = (type) => {
  if (selectedTypes.value.includes(type)) {
    selectedTypes.value = selectedTypes.value.filter((t) => t !== type);
  } else {
    selectedTypes.value.push(type);
  }
};

const confirmEmergencyTypes = () => {
  emergencyTypeSelected.value = true;
};

const submitEmergency = async () => {
  spinnerService.show();
  const emergencyData = {
    types: selectedTypes.value,
    detail: emergencyDetail.value,
    additionalInfo: additionalInfo.value,
    lat: currentLocation.value.latitude,
    lng: currentLocation.value.longitude,
    accuracy: currentLocation.value.accuracy,
  };
  const resp = await emergencyService.sendDistressSignal(emergencyData);
  if (resp.success) {
    toastService.success("Report has been submitted successfully!");
    resetForm();
  } else {
    toastService.error(resp.message);
    setTimeout(() => {
      resetForm();
    }, 5000);
  }
  spinnerService.hide();
};

const resetForm = () => {
  isActivated.value = false;
  emergencyTypeSelected.value = false;
  selectedTypes.value = [];
  emergencyDetail.value = "";
  additionalInfo.value = "";
  pressProgress.value = 0;
};
</script>

<style scoped>
.emergency-option i {
  font-size: 2rem;
  margin-bottom: 0.5rem;
}

/* Base Styles */
.panic-app {
  font-family: "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans",
    "Helvetica Neue", sans-serif;
  max-width: 100%;
  min-height: 100vh;
  background-color: #f8f8f8;
  color: #333;
  display: flex;
  flex-direction: column;
}

.app-header {
  background-color: #d32f2f;
  color: white;
  padding: 1.5rem;
  text-align: center;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
}

.app-header h1 {
  margin: 0;
  font-size: 1.8rem;
}

.subtitle {
  margin: 0.5rem 0 0;
  opacity: 0.9;
  font-size: 1rem;
}

.panic-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1rem;
  max-width: 500px;
  margin: 0 auto;
  width: 100%;
  position: relative;
  flex-grow: 1;
}

/* Panic Button Styles */
.panic-button-wrapper {
  width: 100%;
  max-width: 280px;
  margin: 2rem auto;
  position: relative;
}

.panic-button {
  position: relative;
  width: 280px; /* Fixed width for better control */
  height: 280px; /* Fixed height to match width */
  border-radius: 50%;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: none;
  outline: none;
  box-shadow: 0 0 0 8px rgba(255, 255, 255, 0.2), 0 6px 30px rgba(244, 67, 54, 0.5),
    inset 0 0 20px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  overflow: hidden;
  font-weight: bold;
  font-size: 1.4rem;
  text-align: center;
  text-transform: uppercase;
  margin: 0 auto; /* Center horizontally */
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
}

.panic-button::before {
  content: "";
  position: absolute;
  top: -10%;
  left: -10%;
  right: -10%;
  bottom: -10%;
  border-radius: 50%;
  background: radial-gradient(
    circle at center,
    rgba(255, 255, 255, 0.2) 0%,
    transparent 70%
  );
  z-index: 1;
}

.panic-button .button-inner {
  position: relative;
  z-index: 2;
  padding: 2rem;
  background-color: rgba(134, 132, 132, 0.5);
  border-radius: 50%;
  width: 80%;
  height: 80%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  backdrop-filter: blur(1px);
  border: 2px solid rgba(255, 255, 255, 0.2);
}

.panic-button.pressing {
  transform: scale(0.95);
  box-shadow: 0 3px 10px rgba(244, 67, 54, 0.5);
}

.panic-button.activated {
  background-color: #4caf50;
  box-shadow: 0 6px 20px rgba(76, 175, 80, 0.4);
  animation: none;
}

.press-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 8px;
  background-color: rgba(255, 255, 255, 0.8);
  transition: width 0.1s linear;
}

.button-inner {
  padding: 1rem;
  z-index: 2;
}

/* Floating Hotlines Button */
.floating-hotlines-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  position: relative;
  width: auto;
  margin: 0 auto 1rem;
  padding: 0.75rem 1.5rem;
  background: rgba(255, 255, 255, 0.95);
  border: 2px solid #d32f2f;
  color: #d32f2f;
  border-radius: 50px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  font-weight: 600;
  z-index: 3;
  transition: all 0.3s ease;
  cursor: pointer;
}

.floating-hotlines-btn.hidden {
  opacity: 0;
  transform: translateY(-20px);
  pointer-events: none;
}

/* Emergency Options */
.emergency-options {
  width: 100%;
  margin-top: -1rem;
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.emergency-options h2 {
  margin-top: 0;
  color: #d32f2f;
  font-size: 1.3rem;
  text-align: center;
}

.option-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin: 1.5rem 0;
}

.emergency-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1rem 0.5rem;
  border-radius: 8px;
  border: 2px solid #e0e0e0;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
}

.emergency-option svg {
  font-size: 1.8rem;
  margin-bottom: 0.5rem;
}

.emergency-option.medical {
  color: #d32f2f;
}

.emergency-option.police {
  color: #1976d2;
}

.emergency-option.fire {
  color: #ff6d00;
}

.emergency-option.selected {
  border-color: currentColor;
  background-color: rgba(0, 0, 0, 0.05);
  transform: translateY(-3px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.confirm-btn {
  width: 100%;
  padding: 1rem;
  background-color: #d32f2f;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.3s;
}

.confirm-btn:disabled {
  background-color: #bdbdbd;
  cursor: not-allowed;
}

.confirm-btn:not(:disabled):hover {
  background-color: #b71c1c;
}

/* Emergency Details */
.emergency-details {
  width: 100%;
  margin-top: -1rem;
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.emergency-details h2 {
  margin-top: 0;
  color: #d32f2f;
  font-size: 1.3rem;
  text-align: center;
}

.detail-section {
  margin-bottom: 1.5rem;
}

.detail-section label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #555;
}

.detail-select,
.detail-textarea {
  width: 100%;
  padding: 0.8rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s;
}

.detail-select:focus,
.detail-textarea:focus {
  outline: none;
  border-color: #d32f2f;
}

.detail-textarea {
  min-height: 100px;
  resize: vertical;
}

.submit-btn {
  width: 100%;
  padding: 1rem;
  background-color: #d32f2f;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.3s;
}

.submit-btn:hover {
  background-color: #b71c1c;
}

/* Hotlines Section */
.hotlines-section {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: white;
  z-index: 100;
  padding: 1.5rem;
  overflow-y: auto;
}

.hotlines-section h2 {
  margin-top: 0;
  color: #d32f2f;
  font-size: 1.3rem;
  text-align: center;
}

.hotline-cards {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  margin: 1.5rem 0;
}

.hotline-card {
  padding: 1.5rem;
  border-radius: 8px;
  color: white;
  text-align: center;
}

.hotline-card h3 {
  margin-top: 0;
  font-size: 1.2rem;
}

.hotline-card.medical {
  background-color: #d32f2f;
}

.hotline-card.police {
  background-color: #1976d2;
}

.hotline-card.fire {
  background-color: #ff6d00;
}

.hotline-number {
  display: block;
  font-size: 1.8rem;
  font-weight: bold;
  margin: 1rem 0;
  color: white;
  text-decoration: none;
}

.messenger-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background-color: rgba(255, 255, 255, 0.2);
  padding: 0.5rem 1rem;
  border-radius: 20px;
  color: white;
  text-decoration: none;
  font-weight: 500;
  margin-top: 0.5rem;
  transition: background-color 0.3s;
}

.messenger-link:hover {
  background-color: rgba(255, 255, 255, 0.3);
}

.back-btn {
  width: 100%;
  padding: 1rem;
  background-color: #757575;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.3s;
}

.back-btn:hover {
  background-color: #616161;
}

/* App Footer */
.app-footer {
  padding: 1rem;
  position: sticky;
  bottom: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(5px);
  border-top: 1px solid #eee;
  display: flex;
  justify-content: center;
}

.hotlines-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  color: #d32f2f;
  font-weight: bold;
  cursor: pointer;
  font-size: 1rem;
  padding: 0.5rem 1rem;
}

/* Transitions */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.4s cubic-bezier(0.22, 1, 0.36, 1);
}

.slide-up-enter-from,
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s ease;
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

.overlay-blocker {
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: 2;
  background: transparent;
  pointer-events: none;
}

/* Pulse Animation */
.pulse-animation {
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(244, 67, 54, 0.7);
  }
  70% {
    box-shadow: 0 0 0 20px rgba(244, 67, 54, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(244, 67, 54, 0);
  }
}

/* Responsive Adjustments */
@media (max-width: 480px) {
  .option-grid {
    grid-template-columns: 1fr;
  }

  .panic-button {
    font-size: 1.2rem;
  }
}

@media (min-width: 768px) {
  .panic-button {
    max-width: 300px;
    font-size: 1.5rem;
  }

  .panic-container {
    padding: 2rem;
  }
}
</style>
